import React, { useState } from 'react';
import styled from 'styled-components';
import { FiDollarSign, FiClock, FiUsers, FiChevronDown, FiChevronUp, FiHelpCircle } from 'react-icons/fi';

const Section = styled.section`
  background: ${({ theme }) => theme.colors.light};
  padding: 72px 0 56px 0;
  margin: 0;
  width: 100%;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Title = styled.h2`
  font-size: 2.3rem;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 800;
  margin-bottom: 18px;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.dark};
  text-align: center;
  margin-bottom: 38px;
`;

const BenefitsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 28px;
  justify-content: center;
  margin-bottom: 48px;
`;

const BenefitCard = styled.div`
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 4px 24px rgba(218,1,92,0.08);
  padding: 32px 28px 28px 28px;
  min-width: 260px;
  max-width: 320px;
  flex: 1 1 260px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const IconCircle = styled.div`
  background: ${({ theme }) => theme.colors.light};
  border-radius: 50%;
  width: 54px;
  height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.primary};
`;

const BenefitTitle = styled.h3`
  font-size: 1.18rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.dark};
  margin-bottom: 9px;
`;

const BenefitDesc = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.secondary};
`;

const FAQSection = styled.div`
  margin-top: 32px;
`;

const FAQTitle = styled.h4`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 18px;
`;

const FAQList = styled.div`
  max-width: 700px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(218,1,92,0.04);
  margin-bottom: 14px;
  padding: 0;
`;

const FAQQuestion = styled.button`
  width: 100%;
  background: none;
  border: none;
  outline: none;
  font-size: 1.09rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark};
  padding: 18px 22px;
  text-align: right;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const FAQAnswer = styled.div`
  padding: 0 22px 16px 22px;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 1rem;
  text-align: right;
`;

const faqs = [
  {
    q: 'كيف يتم احتساب العمولات؟',
    a: 'نحن نقدم عمولات تنافسية من بين الأدنى في السوق، وتُحتسب فقط على الرحلات المكتملة.'
  },
  {
    q: 'ما هي متطلبات التسجيل كسائق؟',
    a: 'رخصة قيادة سارية، سيارة بحالة جيدة، واجتياز الفحص الأمني. سنرشدك خلال كل خطوة من التسجيل.'
  },
  {
    q: 'متى يتم الدفع للسائقين؟',
    a: 'تتم المدفوعات شهرياً مباشرة إلى حسابك البنكي أو عبر وسيلة الدفع التي تختارها.'
  },
  {
    q: 'هل يوجد دعم للسائقين؟',
    a: 'نعم! لدينا فريق دعم متواجد لمساعدتك عبر التطبيق أو الهاتف في أي وقت.'
  }
];

const DriverBenefits: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  return (
    <Section id="driver-benefits">
      <Container>
        <Title><span style={{color: '#DA015C'}}>مزايا</span> <span style={{color: '#000000'}}>السائقين</span></Title>
        <Subtitle>انضم إلى مجتمع واصل واستمتع بامتيازات حقيقية تدعم نجاحك وراحتك.</Subtitle>
        <BenefitsRow>
          <BenefitCard>
            <IconCircle><FiDollarSign /></IconCircle>
            <BenefitTitle>عمولات تنافسية</BenefitTitle>
            <BenefitDesc>نسبة عمولة منخفضة لزيادة أرباحك في كل رحلة.</BenefitDesc>
          </BenefitCard>
          <BenefitCard>
            <IconCircle><FiClock /></IconCircle>
            <BenefitTitle>ساعات عمل مرنة</BenefitTitle>
            <BenefitDesc>اختر أوقات العمل المناسبة لك بدون أي التزام زمني.</BenefitDesc>
          </BenefitCard>
          <BenefitCard>
            <IconCircle><FiUsers /></IconCircle>
            <BenefitTitle>مجتمع داعم</BenefitTitle>
            <BenefitDesc>فريق دعم متجاوب وبيئة عمل محفزة للسائقين.</BenefitDesc>
          </BenefitCard>
        </BenefitsRow>
        <DriveButton href="#waitlist">انضم إلى واصل اليوم</DriveButton>
      </Container>
    </Section>
  );
};

const DriveButton = styled.a`
  display: block;
  margin: 0 auto 0 auto;
  background: #DA015C;
  color: #ffffff;
  padding: 1rem 2.5rem;
  border-radius: 40px;
  font-size: 1.18rem;
  font-weight: 800;
  text-align: center;
  text-decoration: none;
  box-shadow: 0 2px 12px rgba(218,1,92,0.09);
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
  margin-top: 10px;
  margin-bottom: 16px;
  cursor: pointer;
  &:hover, &:focus {
    background: #b80149;
    color: #ffffff;
    box-shadow: 0 4px 18px rgba(218,1,92,0.15);
  }
`;

export default DriverBenefits;
